import React, { useState, useEffect } from 'react';
import styles from './ContactPage.module.css';
import SEO from '../components/SEO/SEO';
import StructuredData from '../components/StructuredData/StructuredData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faMapMarkerAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faLinkedin, faTwitter, faFacebook } from '@fortawesome/free-brands-svg-icons';

const ContactPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const emailOptions = [
    {
      icon: faEnvelope,
      label: 'General Inquiries',
      email: 'info@solvedotlabs.com',
      description: 'For general questions and information',
      color: '#007BFF'
    },
    {
      icon: faEnvelope,
      label: 'Sales & Business',
      email: 'sales@solvedotlabs.com',
      description: 'For sales inquiries and partnerships',
      color: '#00BFFF'
    },
    {
      icon: faEnvelope,
      label: 'Support & Help',
      email: 'support@solvedotlabs.com',
      description: 'For technical support and assistance',
      color: '#007BFF'
    },
    {
      icon: faEnvelope,
      label: 'Partnerships',
      email: 'partners@solvedotlabs.com',
      description: 'For partnership and collaboration',
      color: '#007BFF'
    },
    {
      icon: faEnvelope,
      label: 'Technical Support',
      email: 'tech@solvedotlabs.com',
      description: 'For advanced technical issues and escalations',
      color: '#007BFF'
    }
  ];

  return (
    <div className={styles.contactPage}>
      <SEO 
        title="Contact SolveDot Labs | Software Development Company - Get in Touch"
        description="Contact SolveDot Labs (SolveDot Labs Pvt. Ltd.) for AI solutions, website development, IoT systems, robotics, and software development services. Call +91 7838443394, email info@solvedotlabs.com, or visit us in Greater Noida, India. Multiple contact channels available."
        keywords="solve, solvedot, solvedotlabs, solvedot labs, solve dot labs, solvedot labs pvt ltd, Contact SolveDot Labs, Software Development Contact, Greater Noida Software Company, Tech Support, Software Consultation Contact, AI Solutions Contact, Website Development Contact"
      />
      <StructuredData type="contact" />
      {/* Scroll Progress Indicator */}
      <div className={styles.scrollProgressBar}>
        <div 
          className={styles.scrollProgressFill}
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
      {/* Enhanced Hero Header */}
      <section className={styles.heroHeader}>
        <div className={styles.heroBackground}>
          <div className={styles.heroOrb1}></div>
          <div className={styles.heroOrb2}></div>
        </div>
        <div className={styles.heroContent}>
          <span className={styles.heroBadge}>Get In Touch</span>
          <h1 className={styles.heroTitle}>
            Let's Build Your Next Big Thing.
            <br />
            <span className={styles.titleHighlight}>Together.</span>
          </h1>
          <p className={styles.heroDescription}>
            This is where the conversation starts. Tell us about your project, your goals, or the challenges you're facing. We're here to listen and explore how we can help.
          </p>
        </div>
      </section>

      {/* Quick Contact Section */}
      <section className={styles.quickContactSection}>
        <div className={styles.quickContactGrid}>
          <div className={styles.quickContactCard}>
            <div className={styles.contactIconWrapper}>
              <div className={styles.contactIconGlow}></div>
              <FontAwesomeIcon icon={faPhone} className={styles.contactIcon} />
            </div>
            <h3>Call Us</h3>
            <p className={styles.contactValue}>+91 7838 443394</p>
            <a href="tel:+917838443394" className={styles.contactLink}>
              Call Now <span className={styles.arrow}>→</span>
            </a>
          </div>
          <div className={styles.quickContactCard}>
            <div className={styles.contactIconWrapper}>
              <div className={styles.contactIconGlow}></div>
              <FontAwesomeIcon icon={faWhatsapp} className={styles.contactIcon} />
            </div>
            <h3>WhatsApp</h3>
            <p className={styles.contactValue}>Chat with us instantly</p>
            <a 
              href="https://wa.me/917838443394" 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.contactLink}
            >
              Start Chat <span className={styles.arrow}>→</span>
            </a>
          </div>
          <div className={styles.quickContactCard}>
            <div className={styles.contactIconWrapper}>
              <div className={styles.contactIconGlow}></div>
              <FontAwesomeIcon icon={faClock} className={styles.contactIcon} />
            </div>
            <h3>Business Hours</h3>
            <p className={styles.contactValue}>Mon - Fri: 9:00 AM - 6:00 PM</p>
            <p className={styles.contactValue}>Sat: 10:00 AM - 4:00 PM</p>
          </div>
        </div>
      </section>

      {/* Email Options Section */}
      <section className={styles.emailSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Contact Options</span>
          <h2 className={styles.sectionTitle}>Choose the Right Channel</h2>
          <p className={styles.sectionDescription}>
            We have dedicated email addresses for different purposes. Choose the one that best fits your needs.
          </p>
        </div>
        <div className={styles.emailGrid}>
          {emailOptions.map((option, index) => (
            <div key={index} className={styles.emailCard}>
              <div className={styles.emailCardInner}>
                <div className={styles.emailIconWrapper}>
                  <div className={styles.emailIconGlow}></div>
                  <FontAwesomeIcon icon={option.icon} className={styles.emailIcon} />
                </div>
                <h3 className={styles.emailLabel}>{option.label}</h3>
                <p className={styles.emailDescription}>{option.description}</p>
                <div className={styles.emailLinkWrapper}>
                  <a 
                    href={`mailto:${option.email}?subject=Inquiry from Website`}
                    className={styles.emailLink}
                    title={`Send email to ${option.email}`}
                  >
                    <span className={styles.emailAddress}>{option.email}</span>
                    <span className={styles.arrow}>→</span>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Office Info Section */}
      <section className={styles.officeSection}>
        <div className={styles.officeContent}>
          <div className={styles.officeCard}>
            <div className={styles.officeIconWrapper}>
              <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.officeIcon} />
            </div>
            <h3>Our Office</h3>
            <p className={styles.officeAddress}>
              SolveDot Labs<br />
              Habitech Qube Crystal Mall,<br />
              Knowledge Park 3,<br />
              Greater Noida, U.P.,<br />
              India
            </p>
            <a href="https://maps.app.goo.gl/gQ6fHPC8dPH6fGcT7" target="_blank" rel="noopener noreferrer" className={styles.mapLink}>
              View on Map <span className={styles.arrow}>→</span>
            </a>
          </div>
          <div className={styles.socialCard}>
            <h3>Connect With Us</h3>
            <p className={styles.socialDescription}>Follow us on social media for updates and insights.</p>
            <div className={styles.socialLinks}>
              <a 
                href="https://linkedin.com/company/solvedotlabs" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label="LinkedIn"
              >
                <FontAwesomeIcon icon={faLinkedin} />
              </a>
              <a 
                href="https://twitter.com/solvedotlabs" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label="Twitter"
              >
                <FontAwesomeIcon icon={faTwitter} />
              </a>
              <a 
                href="https://facebook.com/solvedotlabs" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label="Facebook"
              >
                <FontAwesomeIcon icon={faFacebook} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
